import React, { useMemo, useState } from 'react';
import { Search, Download, RefreshCw, ChevronLeft, ChevronRight, User, Eye, Calendar, ClipboardList, CheckCircle2, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
// Mock data for referrals history
const MOCK_REFERRALS_HISTORY = [{
  id: 'rh-1',
  episodeId: 'EP-2026-001',
  patientName: 'Ahmed Mansour',
  nationalId: '1098765432',
  insurance: 'Tawuniya',
  status: 'fulfilled',
  itemsCount: 4,
  fulfilledCount: 4,
  date: '2026-06-25T10:30:00Z'
}, {
  id: 'rh-2',
  episodeId: 'EP-2026-005',
  patientName: 'Sara Al-Otaibi',
  nationalId: '1012345678',
  insurance: 'Bupa',
  status: 'partially_fulfilled',
  itemsCount: 3,
  fulfilledCount: 1,
  date: '2026-06-24T14:15:00Z'
}, {
  id: 'rh-3',
  episodeId: 'EP-2026-012',
  patientName: 'Khalid Abdullah',
  nationalId: '1055554444',
  insurance: 'Medgulf',
  status: 'fulfilled',
  itemsCount: 2,
  fulfilledCount: 2,
  date: '2026-06-23T09:00:00Z'
}];
export function ReferralsHistory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const filteredReferrals = useMemo(() => {
    return MOCK_REFERRALS_HISTORY.filter(ref => {
      const query = searchQuery.toLowerCase();
      return ref.episodeId.toLowerCase().includes(query) || ref.patientName.toLowerCase().includes(query) || ref.nationalId.includes(query);
    });
  }, [searchQuery]);
  const totalPages = Math.ceil(filteredReferrals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReferrals = filteredReferrals.slice(startIndex, startIndex + itemsPerPage);
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="space-y-6 pb-12" data-id="element-607">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-608">
        <div data-id="element-609">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-610">
            Pharmacy Referrals History
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-611">
            Track historical referral episodes and fulfillment progress
          </p>
        </div>
        <div className="flex gap-3" data-id="element-612">
          <Button variant="outline" data-id="element-613">
            <RefreshCw className="w-4 h-4 mr-2" data-id="element-614" />
            Refresh
          </Button>
          <Button variant="outline" data-id="element-615">
            <Download className="w-4 h-4 mr-2" data-id="element-616" />
            Export
          </Button>
        </div>
      </div>

      <Card data-id="element-617">
        <CardContent className="pt-6" data-id="element-618">
          <div className="relative max-w-md" data-id="element-619">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-620" />
            <Input placeholder="Search by Episode ID, Patient, or ID..." value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }} className="pl-9" data-id="element-621" />
          </div>
        </CardContent>
      </Card>

      <Card data-id="element-622">
        <CardContent className="p-0" data-id="element-623">
          <div className="overflow-x-auto" data-id="element-624">
            <Table data-id="element-625">
              <TableHeader data-id="element-626">
                <TableRow data-id="element-627">
                  <TableHead data-id="element-628">Episode ID</TableHead>
                  <TableHead data-id="element-629">Patient</TableHead>
                  <TableHead data-id="element-630">Insurance</TableHead>
                  <TableHead data-id="element-631">Fulfillment</TableHead>
                  <TableHead data-id="element-632">Status</TableHead>
                  <TableHead data-id="element-633">Date</TableHead>
                  <TableHead className="text-right" data-id="element-634">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-635">
                {paginatedReferrals.map(ref => <TableRow key={ref.id} className="hover:bg-slate-50" data-id="element-636">
                    <TableCell data-id="element-637">
                      <div className="flex items-center gap-2" data-id="element-638">
                        <ClipboardList className="w-4 h-4 text-blue-500" data-id="element-639" />
                        <span className="font-mono font-bold text-slate-900" data-id="element-640">
                          {ref.episodeId}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-641">
                      <div className="font-medium text-slate-900" data-id="element-642">
                        {ref.patientName}
                      </div>
                      <div className="text-xs text-slate-500 font-mono" data-id="element-643">
                        {ref.nationalId}
                      </div>
                    </TableCell>
                    <TableCell data-id="element-644">
                      <Badge variant="neutral" className="bg-blue-50 text-blue-700 border-blue-100" data-id="element-645">
                        {ref.insurance}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-646">
                      <div className="flex flex-col gap-1 w-32" data-id="element-647">
                        <div className="flex justify-between text-[10px] font-bold text-slate-500" data-id="element-648">
                          <span data-id="element-649">Progress</span>
                          <span data-id="element-650">
                            {Math.round(ref.fulfilledCount / ref.itemsCount * 100)}
                            %
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden" data-id="element-651">
                          <div className="bg-blue-500 h-full rounded-full transition-all duration-500" style={{
                        width: `${ref.fulfilledCount / ref.itemsCount * 100}%`
                      }} data-id="element-652" />
                        </div>
                        <span className="text-[10px] text-slate-400" data-id="element-653">
                          {ref.fulfilledCount} of {ref.itemsCount} items
                        </span>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-654">
                      <Badge variant={ref.status === 'fulfilled' ? 'success' : 'warning'} size="sm" className="capitalize" data-id="element-655">
                        {ref.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell data-id="element-656">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-657">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" data-id="element-658" />
                        {new Date(ref.date).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="text-right" data-id="element-659">
                      <Button variant="ghost" size="sm" data-id="element-660">
                        <Eye className="w-4 h-4 mr-2" data-id="element-661" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </motion.div>;
}